import {
  KpiDashboardKeyAction,
  KpiOperatorKeyActionScore,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'

export const MAX_SELECTED_KEY_ACTION_COUNT = 3
export const OPERATOR_SCORE_COUNT = 10

export interface KeyActionViewModel {
  id: string
  label: string
  name: string
  averageScore: number | null
  averageScoreText: string
  lowScore: boolean
  selected: boolean
}

/** 0 始まりの index から KeyAction の表示ラベルを生成する。 */
export function createKeyActionLabel(index: number): string {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(`KeyAction index is invalid: ${index}`)
  }

  return `KA${String(index + 1).padStart(2, '0')}`
}

/** KeyAction カタログをスコアと選択状態付きの表示モデルに変換する。 */
export function buildKeyActionViewModels(
  keyActions: readonly KpiDashboardKeyAction[],
  scores: readonly KpiOperatorKeyActionScore[],
  selectedKeyActionIds: readonly string[],
): KeyActionViewModel[] {
  const orderedKeyActions = [...keyActions].sort(
    (left, right) => left.orderNo - right.orderNo,
  )
  const knownIds = new Set(orderedKeyActions.map((keyAction) => keyAction.id))
  const unknownSelectedId = selectedKeyActionIds.find((id) => !knownIds.has(id))
  if (unknownSelectedId != null) {
    throw new Error(`Selected KeyAction is missing: ${unknownSelectedId}`)
  }

  const selectedIds = new Set(selectedKeyActionIds)
  return orderedKeyActions.map((keyAction, index) => {
    const score = scores.find(
      (candidate) => candidate.keyActionId === keyAction.id,
    )
    if (score == null) {
      throw new Error(`KeyAction score is missing: ${keyAction.id}`)
    }
    if (typeof score.averageScore === 'undefined') {
      throw new Error(`KeyAction average score is missing: ${keyAction.id}`)
    }

    return {
      id: keyAction.id,
      label: createKeyActionLabel(index),
      name: keyAction.name,
      averageScore: score.averageScore,
      averageScoreText: formatAverageScore(score.averageScore),
      lowScore: score.averageScore != null && score.averageScore < 2.5,
      selected: selectedIds.has(keyAction.id),
    }
  })
}

/** 選択中の KeyAction ID を表示順で返す。 */
export function selectedIdsFromKeyActions(
  keyActions: readonly KeyActionViewModel[],
): string[] {
  return keyActions
    .filter((keyAction) => keyAction.selected)
    .map(({ id }) => id)
}

/** 指定 ID 集合を反映した KeyAction 表示モデルを返す。 */
export function withSelectedKeyActionIds(
  keyActions: readonly KeyActionViewModel[],
  selectedIds: readonly string[],
): KeyActionViewModel[] {
  const knownIds = new Set(keyActions.map(({ id }) => id))
  const unknownId = selectedIds.find((id) => !knownIds.has(id))
  if (unknownId != null) {
    throw new Error(`Selected KeyAction is missing: ${unknownId}`)
  }

  const selectedIdSet = new Set(selectedIds)
  return keyActions.map((keyAction) => ({
    ...keyAction,
    selected: selectedIdSet.has(keyAction.id),
  }))
}

/** 2 つの ID 配列が順序を除いて同じ集合か判定する。 */
export function haveSameIds(
  left: readonly string[],
  right: readonly string[],
): boolean {
  if (left.length !== right.length) {
    return false
  }

  const rightIds = new Set(right)
  return left.every((id) => rightIds.has(id))
}

function formatAverageScore(score: number | null): string {
  return score == null ? '-' : score.toFixed(1)
}
